// ════════════════════════════════════════════════════════════════
// DemoService — 데모 장비 대여 관리
// v7.23 (2026-06-23) — 조회 기능 (목록, 통계, 활성 대여, 회수 이력)
// ════════════════════════════════════════════════════════════════
import { supabase } from '../supabase';

// 카테고리 코드 → 한글 라벨 매핑
export const DEMO_CATEGORY_LABEL = {
  'NAS_DAS': 'NAS / DAS',
  'HDD': 'HDD',
  'SSD': 'SSD',
  'Switch_TB': 'Switch / TB',
  'LAN_Card': 'LAN Card',
  'Memory_Module': 'Memory / Module',
  // v7.336: 위 6종 어디에도 안 들어가는 장비용 (전원 어댑터, 레일 킷, 케이블 등).
  //   UI 는 이 맵을 그대로 순회하므로 여기에만 넣으면 추가/필터/집계가 다 따라온다.
  //   ⚠ demo_equipment.category 에 CHECK 제약이 걸려 있으면 저장이 거부된다.
  //     확인·해제는 migrations/v7.336-demo-category-etc.sql 참고.
  'ETC': '기타',
};

// 카테고리 코드 → 색상
export const DEMO_CATEGORY_COLOR = {
  'NAS_DAS': '#009844',
  'HDD': '#d84315',
  'SSD': '#5d4037',
  'Switch_TB': '#1976d2',
  'LAN_Card': '#7b1fa2',
  'Memory_Module': '#c2185b',
  'ETC': '#607d8b',   // v7.336: 블루그레이 — 기존 6색과 겹치지 않는 무채계열
};

// 상태 코드 → 색상
// v7.246: 앱이 인정하는 장비 상태 5종. 이관 데이터에는 '대여중'·'테스트중' 같은 값이 섞여 있다.
export const DEMO_VALID_STATUS = ['정상작동', '불량', '수리중', '점검중', '확인불가'];
// [회수] 버튼이 비정상 상태값을 정상화할 때 쓰는 값.
//   회수 직후 점검을 먼저 하는 운영으로 바꾸려면 '점검중' 으로만 고치면 된다.
export const DEMO_RESTORE_STATUS = '정상작동';

export const DEMO_STATUS_COLOR = {
  '정상작동': { bg: 'rgba(0,152,68,0.12)', fg: '#009844' },
  '불량': { bg: 'rgba(186,26,26,0.12)', fg: '#ba1a1a' },
  '수리중': { bg: 'rgba(245,124,0,0.12)', fg: '#e65100' },
  '점검중': { bg: 'rgba(255,152,0,0.12)', fg: '#f57c00' },
  '확인불가': { bg: 'rgba(118,119,125,0.12)', fg: '#76777d' },
};

export const DemoService = {
  // ─── 장비 목록 ───
  // filters: { category, locationFilter, statusFilter, search }
  async listEquipment({ category = null, locationFilter = null, statusFilter = null, search = '' } = {}) {
    let q = supabase.from('demo_equipment').select('*').order('category').order('model');
    if (category) q = q.eq('category', category);
    if (locationFilter === 'available') q = q.eq('location', '리버');
    else if (locationFilter === 'loaned') q = q.ilike('location', '대여중%');
    if (statusFilter) q = q.eq('status', statusFilter);
    const safe = (search || '').trim().replace(/[,():]/g, ' ').trim();
    if (safe) {
      q = q.or(`model.ilike.%${safe}%,serial_number.ilike.%${safe}%,model_detail.ilike.%${safe}%,note.ilike.%${safe}%`);
    }
    const { data, error } = await q.limit(5000);
    if (error) return { data: [], error };
    return { data: data || [], error: null };
  },

  // ─── v7.422: 주어진 모델명들 중 demo_equipment 에 '등록된' 모델만 Set 으로 반환 ───
  //   배송시트지에서 데모 품목의 [데모 미등록] 경고 판정용.
  //   ⚠ 존재 여부만 본다(가용 수량 아님) — 그래서 대여예약/대여중(자기 예약분) 이슈가 없다.
  //     이미 대여로 빠져 리버에 없는 모델도 '등록된 모델' 이므로 미등록 경고가 뜨지 않는다.
  //   목록은 배송시트지 한 페이지 분량의 '서로 다른 데모 모델' 이라 수가 적다 → 페이지네이션 불필요.
  async listRegisteredModels(models) {
    const list = [...new Set((models || []).map(m => (m || '').trim()).filter(Boolean))];
    if (list.length === 0) return new Set();
    const { data, error } = await supabase.from('demo_equipment').select('model').in('model', list);
    if (error || !data) return new Set();
    return new Set(data.map(r => (r.model || '').trim()).filter(Boolean));
  },

  // ─── 카테고리별 통계 (대시보드 상단 카드 용) ───
  async getStats() {
    const { data, error } = await supabase.from('demo_equipment').select('category, location, status');
    if (error || !data) return {};
    const stats = {};
    data.forEach(r => {
      const cat = r.category || '기타';
      if (!stats[cat]) stats[cat] = { total: 0, available: 0, loaned: 0, broken: 0, other: 0 };
      stats[cat].total++;
      const isLoaned = r.location && r.location.startsWith('대여중');
      const isAvailable = r.location === '리버' && r.status === '정상작동';
      const isBroken = r.status === '불량' || r.status === '수리중';
      if (isLoaned) stats[cat].loaned++;
      else if (isAvailable) stats[cat].available++;
      else if (isBroken) stats[cat].broken++;
      else stats[cat].other++;
    });
    return stats;
  },

  // ─── 활성 + 예약 대여 목록 ───
  // v7.151: pending (예약) 상태도 함께 반환 — 배송시트지 [출고] 대기 상태의 대여도 UI에 노출
  //         호출부는 rental.status 로 pending / active 구분 가능
  async listActiveRentals() {
    const { data, error } = await supabase.from('demo_rentals')
      .select('*')
      .in('status', ['active', 'pending'])
      .order('shipment_date', { ascending: false });
    if (error) return [];
    return data || [];
  },

  // ─── 회수 이력 ───
  async listReturns({ limit = 50 } = {}) {
    const { data, error } = await supabase.from('demo_returns')
      .select('*')
      .order('return_date', { ascending: false })
      .limit(limit);
    if (error) return [];
    return data || [];
  },

  // ─── 단일 장비 조회 (상세 / 편집용) ───
  async getEquipmentById(id) {
    const { data, error } = await supabase.from('demo_equipment').select('*').eq('id', id).single();
    return { data, error };
  },

  // ─── v7.35: 장비 단일 필드 업데이트 (상태 / 비고 인라인 편집용) ───
  // field는 화이트리스트만 허용 — 'status' | 'note'
  async updateEquipmentField(id, field, value) {
    const ALLOWED = ['status', 'note'];
    if (!ALLOWED.includes(field)) {
      return { data: null, error: { message: `허용되지 않은 필드: ${field}` } };
    }
    if (field === 'status') {
      const ALLOWED_STATUS = ['정상작동', '불량', '수리중', '점검중', '확인불가'];
      if (!ALLOWED_STATUS.includes(value)) {
        return { data: null, error: { message: `허용되지 않은 상태값: ${value}` } };
      }
    }
    const payload = { [field]: value === '' ? null : value };
    const { data, error } = await supabase.from('demo_equipment')
      .update(payload).eq('id', id).select().single();
    return { data, error };
  },

  // ─── v7.337: 카테고리 일괄 변경 ───
  //   updateEquipmentField 의 화이트리스트에 'category' 를 끼워 넣지 않은 이유:
  //     그 함수는 id 하나씩 도는 인라인 편집용이다. 카테고리는 '모델 단위' 로 바꾸는 게
  //     자연스러운데(같은 모델이 서로 다른 카테고리면 목록이 쪼개진다),
  //     한 대씩 N번 호출하면 중간에 실패했을 때 반쪽만 바뀐 상태로 남는다.
  //     → id 배열을 받아 한 번의 update 로 처리한다.
  //   ⚠ 유효한 카테고리 코드인지 여기서 막는다. DB 에 CHECK 제약이 없을 수도 있어서
  //     오타가 들어가면 그 장비가 어느 필터에도 안 걸리고 조용히 사라진다.
  async updateEquipmentCategory(ids, category) {
    const list = (ids || []).filter(Boolean);
    if (list.length === 0) return { data: null, error: { message: '대상이 없습니다' } };
    if (!Object.prototype.hasOwnProperty.call(DEMO_CATEGORY_LABEL, category)) {
      return { data: null, error: { message: `허용되지 않은 카테고리: ${category}` } };
    }
    const { data, error } = await supabase.from('demo_equipment')
      .update({ category }).in('id', list).select();
    return { data, error };
  },

  // ─── 단일 대여 조회 (품목 같이) ───
  async getRentalById(rentalId) {
    const { data: rental, error: e1 } = await supabase.from('demo_rentals').select('*').eq('id', rentalId).single();
    if (e1 || !rental) return { data: null, error: e1 };
    const { data: items } = await supabase.from('demo_rental_items').select('*').eq('rental_id', rentalId);
    return { data: { ...rental, items: items || [] }, error: null };
  },

  // ─── v7.330: 비고만 인라인 수정 (대여로그 더블클릭) ───
  //   updateRental() 은 품목 diff·location 복원까지 도는 무거운 경로다.
  //   비고 한 줄 고치려고 그걸 태우면 부작용 위험만 커진다 → 전용 경로를 둔다.
  //   audit_log 는 남기되, 컬럼이 없을 수도 있으므로(v7.43 이전 스키마)
  //   실패해도 본 저장은 되돌리지 않는다.
  async updateRentalNotes(rentalId, notes, by) {
    if (!rentalId) return { ok: false, msg: 'rentalId 누락' };
    try {
      // ⚠ v7.332: 여기서 audit_log 를 같이 select 하면 안 된다.
      //   그 컬럼이 없는 DB(=v7.43 SQL 미실행)에서는 select 자체가
      //   'column demo_rentals.audit_log does not exist' 로 실패해
      //   정작 비고 저장은 시작도 못 한다. 실제로 그렇게 터졌다.
      //   → 본 저장에 필요한 것만 읽는다. audit_log 는 저장 뒤에 따로 시도한다.
      const { data: cur, error: e1 } = await supabase
        .from('demo_rentals').select('notes').eq('id', rentalId).single();
      if (e1) return { ok: false, msg: e1.message };

      const before = cur?.notes || '';
      const after = (notes || '').trim();
      if (before === after) return { ok: true, unchanged: true };

      const { error } = await supabase
        .from('demo_rentals').update({ notes: after || null }).eq('id', rentalId);
      if (error) return { ok: false, msg: error.message };

      // ── 여기부터는 '되면 좋고' 다. 실패해도 위의 비고 저장은 이미 끝났다.
      //   ⚠ supabase-js 는 오류를 throw 하지 않고 error 로 '돌려준다'.
      //     try/catch 만 둘러선 안 잡힌다. error 를 직접 확인해야 한다.
      let auditLog = null;
      try {
        const { data: a, error: ae } = await supabase
          .from('demo_rentals').select('audit_log').eq('id', rentalId).single();
        if (!ae) {
          const cut = (s) => {
            const t = String(s || '').replace(/\s+/g, ' ').trim();
            if (!t) return '(없음)';
            return t.length > 40 ? t.slice(0, 40) + '…' : t;
          };
          const entry = {
            ts: new Date().toISOString(),
            by: by || '미지정',
            action: 'edit_notes',
            summary: `비고 수정: ${cut(before)} → ${cut(after)}`,
          };
          const next = [...(a?.audit_log || []), entry];
          const { error: ue } = await supabase
            .from('demo_rentals').update({ audit_log: next }).eq('id', rentalId);
          if (!ue) auditLog = next;
        }
      } catch (e) { auditLog = null; }
      return { ok: true, notes: after || null, auditLog };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },

  // ─── v7.42: 대여 수정 (필드 + 품목 diff) ───
  // payload: { rentalId, company, shipment_date, expected_return_date, managers, requester, notes, equipment_ids[] }
  // 동작:
  //   1. demo_rentals UPDATE
  //   2. 기존 demo_rental_items 조회 → 추가/삭제 diff 계산
  //   3. 삭제: demo_rental_items delete + demo_equipment.location → '리버' 복원
  //   4. 추가: demo_rental_items insert + demo_equipment.location → '대여중(업체)' 변경
  //   5. rental_items_text 재생성하여 demo_rentals 업데이트
  async updateRental(payload) {
    const { rentalId } = payload;
    if (!rentalId) return { error: { message: 'rentalId 누락' } };

    // 0. 현재 대여 + 기존 items 가져오기
    const { data: existing } = await this.getRentalById(rentalId);
    if (!existing) return { error: { message: '대여를 찾을 수 없습니다.' } };
    const existingItems = existing.items || [];
    const existingEquipmentIds = new Set(existingItems.map(it => it.equipment_id));
    const newEquipmentIds = new Set(payload.equipment_ids || []);

    // diff 계산
    const toAddIds = [...newEquipmentIds].filter(id => !existingEquipmentIds.has(id));
    const toRemoveItems = existingItems.filter(it => !newEquipmentIds.has(it.equipment_id));
    const toRemoveEquipmentIds = toRemoveItems.map(it => it.equipment_id);

    // 추가할 장비 정보 가져오기
    let toAddEquipments = [];
    if (toAddIds.length > 0) {
      const { data: addEqs, error: addEqErr } = await supabase.from('demo_equipment')
        .select('id, model, serial_number, category, location')
        .in('id', toAddIds);
      if (addEqErr) return { error: addEqErr };
      // v7.151: 이미 다른 곳에서 대여 중/예약된 장비 차단 (예약 상태도 포함)
      const conflicted = (addEqs || []).filter(e => e.location && (e.location.startsWith('대여중') || e.location.startsWith('대여예약')));
      if (conflicted.length > 0) {
        return { error: { message: `다른 곳에서 이미 대여 중/예약된 장비: ${conflicted.map(e=>e.model+(e.serial_number?'·'+e.serial_number:'')).join(', ')}` } };
      }
      toAddEquipments = addEqs || [];
    }

    // v7.151: 현재 대여 status에 따라 location prefix 결정
    //   pending → '대여예약(회사)', active → '대여중(회사)'
    //   returned/cancelled 대여는 편집 케이스 아님 (호출부에서 이미 필터됨)
    const locPrefix = existing.status === 'pending' ? '대여예약' : '대여중';

    // 1. demo_rentals 메타 업데이트 (rental_items_text는 step 4에서 일괄)
    const updateRow = {
      company: payload.company,
      shipment_date: payload.shipment_date,
      expected_return_date: payload.expected_return_date || null,
      managers: payload.managers || null,
      requester: payload.requester || null,
      notes: payload.notes || null,
    };

    // 2. 제거할 items 처리
    if (toRemoveItems.length > 0) {
      const removeItemIds = toRemoveItems.map(it => it.id);
      const { error: delErr } = await supabase.from('demo_rental_items').delete().in('id', removeItemIds);
      if (delErr) return { error: delErr };
      // 해당 장비 location 복원
      if (toRemoveEquipmentIds.length > 0) {
        await supabase.from('demo_equipment').update({ location: '리버' }).in('id', toRemoveEquipmentIds);
      }
    }

    // 3. 추가할 items 처리
    if (toAddEquipments.length > 0) {
      const insertRows = toAddEquipments.map(e => ({
        rental_id: rentalId,
        equipment_id: e.id,
        model: e.model,
        serial_number: e.serial_number,
        category: e.category,
      }));
      const { error: insErr } = await supabase.from('demo_rental_items').insert(insertRows);
      if (insErr) return { error: insErr };
      // 해당 장비 location → 'locPrefix(업체)' (v7.151: pending이면 '대여예약', active면 '대여중')
      const locStr = `${locPrefix}(${payload.company})`;
      await supabase.from('demo_equipment').update({ location: locStr }).in('id', toAddIds);
    }

    // 4. 업체명 변경이 있으면 남아 있는 모든 대여 장비의 location도 동기화
    if (existing.company !== payload.company) {
      const remainingEquipmentIds = [...newEquipmentIds].filter(id => !toAddIds.includes(id));
      if (remainingEquipmentIds.length > 0) {
        const locStr = `${locPrefix}(${payload.company})`;
        await supabase.from('demo_equipment').update({ location: locStr }).in('id', remainingEquipmentIds);
      }
    }

    // 5. rental_items_text 재생성
    const { data: allItems } = await supabase.from('demo_rental_items').select('*').eq('rental_id', rentalId);
    const grouped = {};
    (allItems || []).forEach(it => {
      if (!grouped[it.model]) grouped[it.model] = [];
      if (it.serial_number) grouped[it.model].push(it.serial_number);
    });
    const itemsText = Object.entries(grouped).map(([model, serials]) => {
      if (serials.length === 0) return model;
      return model + '\n' + serials.join('\n');
    }).join('\n\n');
    updateRow.rental_items_text = itemsText;

    const { data: updated, error: upErr } = await supabase.from('demo_rentals')
      .update(updateRow).eq('id', rentalId).select().single();
    if (upErr) return { error: upErr };

    // 6. v7.43: audit_log에 변경 엔트리 push (컬럼 없으면 무음 실패)
    if (payload.auditEntry) {
      try {
        const newLog = [...(existing.audit_log || []), payload.auditEntry];
        await supabase.from('demo_rentals').update({ audit_log: newLog }).eq('id', rentalId);
      } catch (e) { /* audit_log 컬럼 미존재 시 무음 처리 */ }
    }

    return { data: updated, error: null };
  },

  // ─── v7.42: 대여 삭제 (전체 취소) ───
  // 사용 케이스: 잘못 등록한 대여를 통째로 취소
  async deleteRental(rentalId) {
    const { data: existing } = await this.getRentalById(rentalId);
    if (!existing) return { error: { message: '대여를 찾을 수 없습니다.' } };
    const equipmentIds = (existing.items || []).map(it => it.equipment_id).filter(Boolean);
    // items 삭제
    await supabase.from('demo_rental_items').delete().eq('rental_id', rentalId);
    // equipment location 복원
    if (equipmentIds.length > 0) {
      await supabase.from('demo_equipment').update({ location: '리버' }).in('id', equipmentIds);
    }
    // rental 삭제
    const { error } = await supabase.from('demo_rentals').delete().eq('id', rentalId);
    return { data: existing, error };
  },

  // ─── v7.25: 장비 추가 ───
  async addEquipment(payload) {
    // payload: { category, model, serial_number, status, model_detail, location, note, tag, added_by }
    const row = {
      category: payload.category,
      model: payload.model,
      serial_number: payload.serial_number || null,
      status: payload.status || '정상작동',
      model_detail: payload.model_detail || null,
      location: payload.location || '리버',
      note: payload.note || null,
      tag: payload.tag || null,
      added_by: payload.added_by || null,
      added_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('demo_equipment').insert(row).select().single();
    return { data, error };
  },

  // ─── v7.25: 대여 가능 장비 검색 (대여 등록 모달용) ───
  // v7.34: limit 30 → 500 (모델 그룹화 시 시리얼 충분히 보이도록)
  async searchAvailableEquipment({ q = '', category = null, limit = 500 } = {}) {
    let query = supabase.from('demo_equipment')
      .select('id, category, model, serial_number, model_detail, status')
      .eq('location', '리버')
      .eq('status', '정상작동')
      .order('model').limit(limit);
    if (category) query = query.eq('category', category);
    if (q && q.trim()) {
      const safe = q.replace(/[,():]/g, ' ').trim();
      query = query.or(`model.ilike.%${safe}%,serial_number.ilike.%${safe}%,model_detail.ilike.%${safe}%`);
    }
    const { data } = await query;
    return data || [];
  },

  // ─── v7.25: 대여 등록 ───
  // payload: { company, shipment_date, expected_return_date, managers, requester, notes, equipment_ids[] }
  async createRental(payload) {
    if (!payload.equipment_ids || payload.equipment_ids.length === 0) {
      return { error: { message: '대여 품목을 선택하세요.' } };
    }
    // 1. shipment_number 생성: OUT-YYYYMMDD-HHMMSS
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const ship_no = `OUT-${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    // 2. 장비 정보 가져오기 (rental_items_text + items INSERT 용)
    const { data: equipments, error: eqErr } = await supabase.from('demo_equipment')
      .select('id, model, serial_number, category, location')
      .in('id', payload.equipment_ids);
    if (eqErr) return { error: eqErr };
    // 이미 대여중 or 대여예약인 장비 차단 (v7.151: '대여예약' 상태도 차단 대상 추가)
    const alreadyLoaned = equipments.filter(e => e.location && (e.location.startsWith('대여중') || e.location.startsWith('대여예약')));
    if (alreadyLoaned.length > 0) {
      return { error: { message: `이미 대여 중/예약된 장비: ${alreadyLoaned.map(e=>e.model+(e.serial_number?'·'+e.serial_number:'')).join(', ')}` } };
    }
    // 3. rental_items_text 생성 (모델당 시리얼 묶음)
    const grouped = {};
    equipments.forEach(e => {
      if (!grouped[e.model]) grouped[e.model] = [];
      if (e.serial_number) grouped[e.model].push(e.serial_number);
    });
    const itemsText = Object.entries(grouped).map(([model, serials]) => {
      if (serials.length === 0) return model;
      return model + '\n' + serials.join('\n');
    }).join('\n\n');
    // 4. demo_rentals INSERT
    // v7.151: status='pending' 으로 시작 — 배송시트지에서 [출고] 처리 시 finalizeRental이 'active'로 승격
    const { data: rental, error: rErr } = await supabase.from('demo_rentals').insert({
      shipment_number: ship_no,
      company: payload.company,
      shipment_date: payload.shipment_date,
      expected_return_date: payload.expected_return_date || null,
      managers: payload.managers || null,
      requester: payload.requester || null,
      notes: payload.notes || null,
      status: 'pending',
      rental_items_text: itemsText,
    }).select().single();
    if (rErr) return { error: rErr };
    // 5. demo_rental_items INSERT
    const itemRows = equipments.map(e => ({
      rental_id: rental.id,
      equipment_id: e.id,
      model: e.model,
      serial_number: e.serial_number,
      category: e.category,
    }));
    const { error: iErr } = await supabase.from('demo_rental_items').insert(itemRows);
    if (iErr) {
      // rollback rental
      await supabase.from('demo_rentals').delete().eq('id', rental.id);
      return { error: iErr };
    }
    // 6. equipment location → '대여예약(업체)' (v7.151: '대여중' 아닌 예약 상태 — 중복 등록 차단용)
    const locStr = `대여예약(${payload.company})`;
    await supabase.from('demo_equipment')
      .update({ location: locStr })
      .in('id', payload.equipment_ids);
    // 7. v7.43: audit_log 초기 엔트리 (컬럼 없으면 무음 실패)
    try {
      const auditEntry = {
        ts: new Date().toISOString(),
        by: payload.managers || payload.requester || '미지정',
        action: 'create',
        summary: `대여 예약 등록 (${payload.equipment_ids.length}대) — 배송시트지 [출고] 대기`,
      };
      await supabase.from('demo_rentals').update({ audit_log: [auditEntry] }).eq('id', rental.id);
    } catch (e) { /* audit_log 컬럼 미존재 시 무음 처리 */ }
    return { data: rental, error: null };
  },

  // ─── v7.151: pending 대여 → active 승격 (배송시트지 [출고] 완료 시 호출) ───
  // payload: { rentalId, by }
  // status: 'pending' → 'active', equipment.location: '대여예약' → '대여중'
  //   ・ 대상 대여가 'pending'이 아니면 조용히 성공 반환 (재호출 안전 idempotent)
  //   ・ 실패해도 배송시트지 fulfilled 처리는 이미 성공한 상태이므로 롤백하지 않음 (경고만)
  async finalizeRental({ rentalId, by }) {
    if (!rentalId) return { ok: false, msg: 'rentalId 누락' };
    try {
      const { data: rental } = await supabase.from('demo_rentals').select('id, status, company').eq('id', rentalId).single();
      if (!rental) return { ok: false, msg: '대여 정보 없음' };
      // 이미 active 이거나 returned/cancelled 이면 status 갱신 안 함 (idempotent)
      if (rental.status === 'pending') {
        await supabase.from('demo_rentals')
          .update({ status: 'active' })
          .eq('id', rentalId)
          .eq('status', 'pending');   // Optimistic: 다른 세션에서 이미 변경했으면 no-op
        // equipment location: '대여예약(회사)' → '대여중(회사)'
        const { data: items } = await supabase.from('demo_rental_items').select('equipment_id').eq('rental_id', rentalId);
        const equipIds = (items || []).map(i => i.equipment_id).filter(Boolean);
        if (equipIds.length > 0) {
          await supabase.from('demo_equipment')
            .update({ location: `대여중(${rental.company})` })
            .in('id', equipIds);
        }
      }
      // audit_log entry
      const { data: existing } = await supabase.from('demo_rentals').select('audit_log').eq('id', rentalId).single();
      const entry = {
        ts: new Date().toISOString(),
        by: by || '미지정',
        action: 'ship_complete',
        summary: '출고 완료 — 대여 시작',
      };
      const newLog = [...(existing?.audit_log || []), entry];
      await supabase.from('demo_rentals').update({ audit_log: newLog }).eq('id', rentalId);
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },

  // ─── v7.151: pending 대여 완전 취소 (배송시트지에서 outbound 삭제 시 연동 호출) ───
  // status 'pending' 인 대여만 취소 가능. active 이면 거부 (이미 출고된 것).
  //   ・ demo_rental_items 삭제
  //   ・ demo_equipment.location 원복 ('리버')
  //   ・ demo_rentals hard delete (pending 흔적 남기지 않음 — 없던 일 취급)
  async cancelPendingRental({ rentalId }) {
    if (!rentalId) return { ok: false, msg: 'rentalId 누락' };
    try {
      const { data: rental } = await supabase.from('demo_rentals').select('id, status').eq('id', rentalId).single();
      if (!rental) return { ok: true, msg: '대여 정보 없음 (이미 삭제됨)' };  // idempotent
      if (rental.status !== 'pending') {
        return { ok: false, msg: `pending 상태만 취소 가능 (현재: ${rental.status})` };
      }
      // 1. equipment location 원복
      const { data: items } = await supabase.from('demo_rental_items').select('equipment_id').eq('rental_id', rentalId);
      const equipIds = (items || []).map(i => i.equipment_id).filter(Boolean);
      if (equipIds.length > 0) {
        await supabase.from('demo_equipment').update({ location: '리버' }).in('id', equipIds);
      }
      // 2. rental_items 삭제
      await supabase.from('demo_rental_items').delete().eq('rental_id', rentalId);
      // 3. rental 삭제
      await supabase.from('demo_rentals').delete().eq('id', rentalId);
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },

  // ─── v7.25: 회수 처리 ───
  // payload: { rental_id, return_date, managers, notes }
  async processReturn(payload) {
    const { rental_id } = payload;
    // 1. 대여 정보 + 품목
    const { data: rental } = await supabase.from('demo_rentals').select('*').eq('id', rental_id).single();
    if (!rental) return { error: { message: '대여 정보를 찾을 수 없습니다.' } };
    if (rental.status !== 'active') return { error: { message: `이미 ${rental.status === 'returned' ? '회수' : '취소'}된 대여입니다.` } };
    const { data: items } = await supabase.from('demo_rental_items').select('equipment_id').eq('rental_id', rental_id);
    // 2. return_number 생성
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const ret_no = `RET-${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    // 3. demo_returns INSERT
    const { error: retErr } = await supabase.from('demo_returns').insert({
      rental_id,
      return_number: ret_no,
      original_shipment_number: rental.shipment_number,
      company: rental.company,
      rental_items_text: rental.rental_items_text,
      shipment_date: rental.shipment_date,
      return_date: payload.return_date,
      managers: payload.managers || rental.managers,
      notes: payload.notes || null,
    });
    if (retErr) return { error: retErr };
    // 4. 대여 상태 → returned
    await supabase.from('demo_rentals')
      .update({ status: 'returned', actual_return_date: payload.return_date, returned_at: new Date().toISOString() })
      .eq('id', rental_id);
    // 5. 장비 location → '리버' 복원
    // v7.245: 여기가 조용히 실패하던 자리다.
    //   equipment_id 가 null 이거나 demo_rental_items 행 자체가 없는 레거시 대여
    //   (OUT-LEGACY-* 등 이관 데이터)에서는 복원할 대상이 0건인데도 '회수 완료'만 떴다.
    //   → 실제로 몇 대를 되돌렸는지 세어서 호출부에 알린다. 판단은 호출부가 한다.
    const itemRows = (items || []).length;
    const equip_ids = (items || []).map(i => i.equipment_id).filter(Boolean);
    let restored = 0;
    let restoreErr = null;
    if (equip_ids.length > 0) {
      const { data: updated, error: eqErr } = await supabase.from('demo_equipment')
        .update({ location: '리버' }).in('id', equip_ids).select('id');
      if (eqErr) restoreErr = eqErr.message;
      else restored = (updated || []).length;
    }
    return {
      data: { return_number: ret_no, itemRows, linked: equip_ids.length, restored, restoreErr },
      error: null,
    };
  },

  // ─── v7.252: finalizeRental 의 역함수 — 출고완료를 되돌릴 때 대여도 함께 원복 ───
  //   배송시트지에서 '실수로 [제품 출고]를 누른' 데모 대여 건을 되돌리기 위한 경로.
  //   재고만 되돌리면 "출고요청인데 대여는 active, 장비는 대여중" 인 엇갈림이 남는다.
  //   ⚠ 이미 회수(returned)/취소(cancelled)된 대여는 거부한다 — 그 뒤를 되돌리면
  //      회수 기록과 어긋난다. 되돌리려면 회수 건을 먼저 정리해야 한다.
  async revertRental({ rentalId, by }) {
    if (!rentalId) return { ok: false, msg: 'rentalId 누락' };
    try {
      const { data: rental } = await supabase.from('demo_rentals')
        .select('id, status, company').eq('id', rentalId).single();
      if (!rental) return { ok: false, msg: '대여 정보를 찾을 수 없습니다.' };
      if (rental.status === 'returned') {
        return { ok: false, msg: '이미 회수 처리된 대여입니다.\n회수 기록을 먼저 정리해주세요.' };
      }
      if (rental.status === 'cancelled') {
        return { ok: false, msg: '이미 취소된 대여입니다.' };
      }

      if (rental.status === 'active') {
        await supabase.from('demo_rentals')
          .update({ status: 'pending' })
          .eq('id', rentalId)
          .eq('status', 'active');   // Optimistic: 그 사이 바뀌었으면 no-op
        // equipment location: '대여중(회사)' → '대여예약(회사)'
        //   현재 '대여중(회사)' 인 것만 되돌린다 — 다른 곳으로 옮겨둔 장비를 덮어쓰지 않도록
        const { data: items } = await supabase.from('demo_rental_items')
          .select('equipment_id').eq('rental_id', rentalId);
        const equipIds = (items || []).map(i => i.equipment_id).filter(Boolean);
        if (equipIds.length > 0) {
          await supabase.from('demo_equipment')
            .update({ location: `대여예약(${rental.company})` })
            .in('id', equipIds)
            .eq('location', `대여중(${rental.company})`);
        }
      }

      const { data: existing } = await supabase.from('demo_rentals')
        .select('audit_log').eq('id', rentalId).single();
      const entry = {
        ts: new Date().toISOString(),
        by: by || '미지정',
        action: 'ship_revert',
        summary: '출고 되돌림 — 대여 예약 상태로 복귀',
      };
      const newLog = [...(existing?.audit_log || []), entry];
      await supabase.from('demo_rentals').update({ audit_log: newLog }).eq('id', rentalId);
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },

  // ─── v7.246: 장비 단건 '리버' 복원 ([회수] 버튼) ───
  //   대여 연결이 끊긴 이관 데이터를 손으로 수습하기 위한 경로.
  //   ⚠ 진행 중인 대여(active/pending)에 묶인 장비는 거부한다 —
  //      그건 대여로그의 [회수 처리]로 풀어야 rental 상태까지 함께 정리된다.
  //      여기서 위치만 바꾸면 rental 은 active 인데 장비는 리버인 엇갈림이 생긴다.
  async restoreEquipmentToRiver(id) {
    try {
      const { data: eq, error: eqErr } = await supabase.from('demo_equipment')
        .select('id, serial_number, status, location').eq('id', id).single();
      if (eqErr || !eq) return { ok: false, msg: '장비를 찾을 수 없습니다.' };

      const { data: links } = await supabase.from('demo_rental_items')
        .select('rental_id').eq('equipment_id', id);
      const rentalIds = (links || []).map(l => l.rental_id).filter(Boolean);
      if (rentalIds.length > 0) {
        const { data: live } = await supabase.from('demo_rentals')
          .select('id, shipment_number, company, status')
          .in('id', rentalIds).in('status', ['active', 'pending']);
        if (live && live.length > 0) {
          const r = live[0];
          return {
            ok: false, blocked: true,
            msg: `진행 중인 대여에 묶여 있는 장비입니다.\n\n출고번호: ${r.shipment_number || '—'}\n업체: ${r.company || '—'}\n상태: ${r.status === 'pending' ? '출고 대기' : '대여중'}\n\n[대여로그]에서 해당 건을 회수 처리해주세요.\n(여기서 위치만 바꾸면 대여 기록과 어긋납니다)`,
          };
        }
      }

      // status 는 비정상값일 때만 정상화한다 — 멀쩡한 '불량'/'수리중'을 덮어쓰지 않기 위해
      const patch = { location: '리버' };
      const normalized = !DEMO_VALID_STATUS.includes(eq.status);
      if (normalized) patch.status = DEMO_RESTORE_STATUS;

      const { data, error } = await supabase.from('demo_equipment')
        .update(patch).eq('id', id).select().single();
      if (error) return { ok: false, msg: error.message };
      return { ok: true, data, normalized, prevStatus: eq.status, prevLocation: eq.location };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },

  // ─── v7.127: 대여로그 audit 엔트리 append (외부에서 호출) ───
  // 배송시트지에서 데모 출고완료 시 호출됨
  // payload: { rentalId, action, summary, by }
  async appendRentalAudit({ rentalId, action, summary, by }) {
    if (!rentalId) return { ok: false, msg: 'rentalId 누락' };
    try {
      const { data: existing } = await supabase.from('demo_rentals').select('audit_log').eq('id', rentalId).single();
      const entry = {
        ts: new Date().toISOString(),
        by: by || '미지정',
        action: action || 'ship_complete',
        summary: summary || '출고 완료',
      };
      const newLog = [...(existing?.audit_log || []), entry];
      const { error } = await supabase.from('demo_rentals').update({ audit_log: newLog }).eq('id', rentalId);
      if (error) return { ok: false, msg: error.message };
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.message || String(e) };
    }
  },
};
